import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api, mediaUrl } from "../lib/api";

const StatusPill = ({ status }) => {
  const map = {
    creating: "text-zinc-400 bg-white/5",
    in_progress: "text-yellow-400 bg-yellow-500/10",
    stitching: "text-yellow-400 bg-yellow-500/10",
    done: "text-emerald-400 bg-emerald-500/10",
    failed: "text-red-400 bg-red-500/10",
  };
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-mono ${
        map[status] || map.creating
      }`}
    >
      {status}
    </span>
  );
};

/**
 * Full-detail panel for a clip/reel: all configuration + per-shot breakdown.
 * For reels: provides "Use as starting point" to copy shots into ReelComposer.
 */
export default function ClipDetailsModal({ item, kind, onClose, onClone, onPlay }) {
  const [reelData, setReelData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (kind === "reel" && item?.id) {
      api.get(`/reels/${item.id}`).then(({ data }) => {
        if (!cancelled) setReelData(data);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [kind, item?.id]);

  if (!item) return null;

  const data = kind === "reel" ? reelData || item : item;
  const isReel = kind === "reel";
  const finalUrl = isReel
    ? data.final_video_url && mediaUrl(data.final_video_url)
    : data.video_url && mediaUrl(data.video_url);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-[#0A0A0A] border border-white/10 rounded-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            {isReel ? (
              <Icons.Layers size={14} className="text-zinc-400" />
            ) : (
              <Icons.Film size={14} className="text-zinc-400" />
            )}
            <div className="font-display text-lg text-white truncate">
              {isReel
                ? data.title
                : data.template_name || "Custom clip"}
            </div>
            <StatusPill status={data.status} />
          </div>
          <div className="flex items-center gap-2">
            {isReel && (
              <button
                onClick={() => onClone(data)}
                className="text-xs text-white border border-white/20 rounded-lg px-3 py-1.5 hover:bg-white/10 flex items-center gap-1.5"
                data-testid="details-clone"
              >
                <Icons.Copy size={12} /> Use as starting point
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10"
            >
              <Icons.X size={16} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
          {finalUrl && (
            <div className="bg-black rounded-lg overflow-hidden">
              <video
                src={finalUrl}
                controls
                className="w-full"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Top-level config */}
          <Section title="Configuration">
            <Row label="Model" value={data.model} mono />
            {!isReel && (
              <>
                <Row label="Duration" value={`${data.duration}s`} mono />
                <Row label="Aspect" value={data.aspect_ratio} mono />
                <Row
                  label="Intensity"
                  value={data.intensity || "subtle"}
                  mono
                />
                <Row
                  label="Camera fixed"
                  value={data.camera_fixed ? "yes" : "no"}
                  mono
                />
              </>
            )}
            {isReel && (
              <>
                <Row label="Aspect" value={data.aspect_ratio} mono />
                <Row label="Shots" value={data.shot_job_ids?.length} mono />
                {data.voiceover_text && (
                  <Row label="Voice-over" value="yes" mono />
                )}
              </>
            )}
            <Row label="Created" value={fmtDate(data.created_at)} mono />
            {data.error && (
              <Row label="Error" value={data.error} error />
            )}
          </Section>

          {/* Single clip prompt */}
          {!isReel && (
            <Section title="Prompt sent to Seedance">
              <pre className="bg-black/40 border border-white/10 rounded-lg p-3 text-[11px] text-zinc-300 font-mono whitespace-pre-wrap leading-relaxed">
                {data.prompt}
              </pre>
              {data.user_prompt && data.user_prompt !== data.prompt && (
                <details className="mt-2">
                  <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-zinc-300">
                    Show original (before intensity prefix)
                  </summary>
                  <pre className="mt-2 bg-black/40 border border-white/10 rounded-lg p-3 text-[11px] text-zinc-500 font-mono whitespace-pre-wrap">
                    {data.user_prompt}
                  </pre>
                </details>
              )}
            </Section>
          )}

          {/* Reel shot breakdown */}
          {isReel && data.shots && (
            <Section title={`Shots (${data.shots.length})`}>
              <div className="space-y-2">
                {data.shots.map((s, i) => (
                  <ShotRow
                    key={s?.id || i}
                    shot={s}
                    index={i}
                    onPlay={onPlay}
                  />
                ))}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
        {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, mono, error }) {
  return (
    <div className="flex gap-3 text-xs">
      <div className="w-32 text-zinc-500 shrink-0">{label}</div>
      <div
        className={`flex-1 ${mono ? "font-mono" : ""} ${
          error ? "text-red-400" : "text-zinc-200"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function ShotRow({ shot, index, onPlay }) {
  if (!shot)
    return (
      <div className="bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-zinc-600">
        Shot #{index + 1} missing
      </div>
    );
  const url = shot.video_url ? mediaUrl(shot.video_url) : null;
  const img = shot.image_url ? mediaUrl(shot.image_url) : null;
  return (
    <div className="bg-black/40 border border-white/10 rounded-lg p-3">
      <div className="flex gap-3">
        <div className="relative w-32 aspect-video rounded overflow-hidden bg-black shrink-0">
          {url ? (
            <video src={url} className="w-full h-full object-cover" muted />
          ) : img ? (
            <img
              src={img}
              alt=""
              className="w-full h-full object-cover opacity-60"
            />
          ) : null}
          {url && (
            <button
              onClick={() => onPlay({ url, title: shot.template_name })}
              className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100"
            >
              <Icons.Play size={20} className="text-white" />
            </button>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 rounded bg-white/10 text-[10px] font-mono flex items-center justify-center text-white">
              {index + 1}
            </div>
            <div className="text-xs text-white font-medium truncate">
              {shot.template_name || "Custom"}
            </div>
            <StatusPill status={shot.status} />
          </div>
          <div className="text-[10px] text-zinc-600 font-mono mb-1">
            {shot.duration}s · intensity:{" "}
            {shot.intensity || "subtle"}
            {shot.camera_fixed && " · camera-locked"}
          </div>
          <div className="text-[10px] text-zinc-400 font-mono leading-snug line-clamp-3">
            {shot.user_prompt || shot.prompt}
          </div>
          {shot.error && (
            <div className="text-[10px] text-red-400 mt-1 line-clamp-2">
              {shot.error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
